import { Sheet } from "@cntrl/ui";
import { pushToast } from "../../app/toasts.js";
import { placeMapsUrl, placeQuery, uberRideUrl, lyftRideUrl, type PlaceRef } from "./dayOfShow.js";
import styles from "./dayOfShow.module.scss";

/** Open a consumer app / web link in a new context. */
function open(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * The tap-actions sheet for a venue or hotel (Ryan, ART-174) — a bottom sheet listing Copy Address, Copy
 * Confirmation Code (a hotel that has one), Open Address (maps), Open in Lyft and Open in Uber. `Sheet` is theme-aware, so inside the forced-dark DOS it
 * renders dark for free. An action with no link to build (no address AND no coordinates) is withheld rather
 * than shown inert — Open Address / Uber can both be absent; Copy and Lyft always have something to do.
 */
export function PlaceActionsSheet({ place, onClose }: { place: PlaceRef; onClose: () => void }) {
  const query = placeQuery(place);
  const maps = placeMapsUrl(place);
  const uber = uberRideUrl(place);
  const lyft = lyftRideUrl(place);

  const confirmation = place.confirmationCode?.trim() ?? "";
  const copy = async (text: string, done: string) => {
    try {
      await navigator.clipboard.writeText(text);
      pushToast(done, "success");
    } catch {
      pushToast("Copy failed — try again.");
    }
    onClose();
  };
  const go = (url: string) => {
    open(url);
    onClose();
  };

  return (
    <Sheet title={place.name || "Actions"} onClose={onClose}>
      <div className={styles.placeSheet} data-testid="place-actions">
        <span className={styles.placeSheetTitle}>{place.name || "Location"}</span>
        <div className={styles.placeActions}>
          {query && (
            <button type="button" className={styles.placeAction} onClick={() => void copy(query, "Address copied.")}>
              Copy Address
            </button>
          )}
          {/* A hotel's confirmation code — the thing you read out at the front desk, and on the card it sits inside
              a button, so it cannot be selected there. Only when the booking has one. */}
          {confirmation && (
            <button
              type="button"
              className={styles.placeAction}
              onClick={() => void copy(confirmation, "Confirmation code copied.")}
            >
              Copy Confirmation Code
            </button>
          )}
          {maps && (
            <button type="button" className={styles.placeAction} onClick={() => go(maps)}>
              Open Address
            </button>
          )}
          <button type="button" className={styles.placeAction} onClick={() => go(lyft)}>
            Open in Lyft
          </button>
          {uber && (
            <button type="button" className={styles.placeAction} onClick={() => go(uber)}>
              Open in Uber
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}
